import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

const SubscriptionGuard = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const { isAuthenticated, getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      // Skip subscription check for partner_admin users
      const roles = user?.['https://eternavaults.com/roles'] || [];
      if (roles.includes('partner_admin')) {
        console.log('Partner admin detected - skipping subscription check');
        setHasActiveSubscription(true); // Partners don't need subscriptions
        setIsLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently({ audience: 'https://api.eternavaults.com' });

        const response = await fetch(`${API_BASE_URL}/api/subscription/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Continuity Handshake check must happen BEFORE partner bypass
          if (data.needsHandshake && window.location.pathname !== '/continuity-activation') {
            navigate('/continuity-activation');
            setIsLoading(false);
            return;
          }

          // Partner-created families don't need subscriptions
          if (data.partnerFamily || data.partner) {
            console.log('Partner family detected - skipping subscription check');
            setHasActiveSubscription(true);
            setIsLoading(false);
            return;
          }

          if (!data.active) {
            console.log('No active subscription found, redirecting to choose-plan');

            // If trial expired, pass that info to the choose-plan page
            if (data.trialExpired) {
              navigate('/choose-plan?trialExpired=true');
            } else {
              navigate('/choose-plan');
            }
            return;
          }

          // SUCCESS: Subscription is active
          console.log('✅ Active subscription confirmed');
          setHasActiveSubscription(true);
        } else {
          // If we hit a rate limit on subscription checks, send to dedicated Rate Limit page
          if (response.status === 429) {
            console.warn('Rate limit hit while checking subscription status, redirecting to /rate-limit');
            navigate('/rate-limit');
          } else {
            console.error('Failed to check subscription status');
            navigate('/choose-plan');
          }
          return;
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        // Only send to rate-limit page for actual rate limit (429 or message), not for every error
        if (error?.status === 429 || /too many requests|rate limit/i.test(error?.message || '')) {
          navigate('/rate-limit');
        } else {
          navigate('/');
        }
        return;
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, [isAuthenticated, getAccessTokenSilently, navigate, user]);

  // Show loading spinner while checking subscription
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #18131a 0%, #1a161f 25%, #18131a 50%, #1f1a24 75%, #18131a 100%)',
        color: '#e2c89c',
        fontFamily: "'Cinzel', serif",
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Ambient light effect */}
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '30%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(226, 200, 156, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'pulse 3s ease-in-out infinite'
        }}></div>
        <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '3px solid rgba(226, 200, 156, 0.4)',
            borderTop: '3px solid #e2c89c',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1.5rem',
            boxShadow: '0 0 20px rgba(226, 200, 156, 0.3)'
          }}></div>
          <p style={{
            fontSize: '1.1rem',
            letterSpacing: '0.1em',
            color: '#e2c89c',
            margin: 0,
            fontWeight: 300
          }}>
            Confirming your vault access…
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 0.3;
              transform: scale(1);
            }
            50% {
              opacity: 0.6;
              transform: scale(1.2);
            }
          }
        `}</style>
      </div>
    );
  }

  // Only render children if user has active subscription
  if (hasActiveSubscription) {
    return children;
  }

  // Fallback: if no redirect happened and no subscription, show a recovery UI instead of blank screen
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      background: 'linear-gradient(135deg, #18131a 0%, #1a161f 25%, #18131a 50%, #1f1a24 75%, #18131a 100%)',
      color: '#e2c89c',
      fontFamily: "'Cinzel', serif",
      textAlign: 'center',
      padding: '2rem'
    }}>
      <div>
        <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem' }}>
          We're having trouble verifying your access. Please try again.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: 'linear-gradient(135deg, #bfa97a 0%, #a8956a 100%)',
            color: '#0B0B0C',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            marginRight: '1rem'
          }}
        >
          Retry
        </button>
        <button
          onClick={() => window.location.href = '/'}
          style={{
            background: 'transparent',
            color: '#e2c89c',
            border: '1px solid rgba(191, 169, 122, 0.4)',
            padding: '0.75rem 2rem',
            borderRadius: '6px',
            fontSize: '1rem',
            cursor: 'pointer'
          }}
        >
          Go Home
        </button>
      </div>
    </div>
  );
};

export default SubscriptionGuard;
